import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import _startCase from 'lodash/startCase';
import _toLower from 'lodash/toLower';

import FormStepper from '../../components/FormStepper';
import ControlledForm from '../../components/ControlledForm';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { useCabinEventReportData } from '../../core/context/CabinEventReportDataContext';

import { CabinEventReport } from '../../core/api/clients';

import { getColor } from '../../core/colors';

import useNavigateWithConfirm from '../../core/hooks/useNavigateWithConfirm';
import { useGlobalFormState } from '../../core/context/GlobalFormStateContext';

import CabinEventFormValidationSchema from './CabinEventFormValidationSchema';

import FlightStep from './FormSteps/FlightStep';
import EventStep from './FormSteps/EventStep';
import NarrativeStep from './FormSteps/NarrativeStep';
import { FormValidationProvider } from '../../core/context/FormValidationContext';

function CabinEventFormEdit(): JSX.Element {
  const authorizedFormData = useAuthorizedFormData();
  const { cabinEventReport, update, updateStatus } = useCabinEventReportData();
  const { setGlobalFormState } = useGlobalFormState();
  const [submitError, setSubmitError] = React.useState<boolean>(false);

  const navigate = useNavigate();
  const navigateWithConfirm = useNavigateWithConfirm();

  const {
    formState: {
      isDirty,
      dirtyFields,
      ...formStateRest
    },
    ...formMethods
  } = useForm<CabinEventReport>({
    defaultValues: cabinEventReport,
    mode: 'onBlur',
    resolver: yupResolver(CabinEventFormValidationSchema),
  });

  React.useEffect(() => {
    formMethods.reset(cabinEventReport);
  }, [cabinEventReport]);

  React.useEffect(() => {
    const { lastModifiedBy } = formMethods.getValues();
    const isSystemUser = lastModifiedBy === 'System';

    setGlobalFormState(isDirty, isSystemUser);
  }, [isDirty, formMethods]);

  const handleSave = () => update(formMethods.getValues());

  const onSubmit = () => {
    setSubmitError(false);
    update(formMethods.getValues(), () => {
      navigate(`/form/cabin-event/${cabinEventReport.id}/review`);
    });
  };

  const onError = () => {
    setSubmitError(true);
  };

  const isRevenueType = () => {
    const formValues = formMethods.getValues();
    return formValues.typeOfFlight === 'Revenue (part 121)';
  };

  return (
    <ControlledForm
      onSubmit={formMethods.handleSubmit(onSubmit, onError)}
      formProps={{
        noValidate: true,
      }}
    >
      <FormStepper
        control={formMethods.control}
        color={getColor(authorizedFormData.metadata?.color ?? 'grey')}
        steps={['Flight & Crew', 'Event', 'Narrative']}
        schema={CabinEventFormValidationSchema.describe()}
        title="Cabin Event Report"
        reportedBy={`${cabinEventReport.employeeName} [${cabinEventReport.employeeId}], ${_startCase(_toLower(cabinEventReport.employeeTitle ?? ''))}, ${cabinEventReport.employeeLocation}`}
        enableAutoSave={cabinEventReport.lastModifiedBy !== 'System'}
        onSave={handleSave}
        saveStatus={updateStatus}
        lastSaved={cabinEventReport.lastModifiedBy === 'System' ? null : cabinEventReport.lastModifiedOn}
        isDirty={isDirty}
        onClose={() => navigateWithConfirm(authorizedFormData.metadata?.route ?? '')}
        submitError={submitError}
      >
        {({ activeStep }) => (
          <FormProvider formState={{ isDirty, dirtyFields, ...formStateRest }} {...formMethods}>
            <FormValidationProvider<CabinEventReport>>
              <FlightStep visible={activeStep === 0} />
              <EventStep visible={activeStep === 1} />
              <NarrativeStep visible={activeStep === 2} />
            </FormValidationProvider>
          </FormProvider>
        )}
      </FormStepper>
    </ControlledForm>
  );
}

export default CabinEventFormEdit;
